import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { CalendarIcon, Users, ArrowRight } from "lucide-react";

const BookingStepEvent = ({
  formData,
  date,
  blockedDates = [],
  onGuestCountChange,
  onDateSelect,
  onUpdateField,
  onNext,
  isValid,
}) => {
  const { t } = useTranslation();

  const blockedDateSet = new Set(blockedDates);

  const isDateDisabled = (d) => {
    if (d < new Date()) return true;
    const dateStr = format(d, "yyyy-MM-dd");
    return blockedDateSet.has(dateStr);
  };

  const eventTypes = [
    { value: "Wedding", label: t("booking.eventTypes.wedding") },
    { value: "Corporate Event", label: t("booking.eventTypes.corporate") },
    { value: "Birthday Party", label: t("booking.eventTypes.birthday") },
    { value: "Traditional Ceremony", label: t("booking.eventTypes.traditional") },
    { value: "Anniversary", label: t("booking.eventTypes.anniversary") },
    { value: "Graduation", label: t("booking.eventTypes.graduation") },
    { value: "Private Dinner", label: t("booking.eventTypes.privateDinner") },
    { value: "Other", label: t("booking.eventTypes.other") },
  ];

  return (
    <Card className="bg-white border-0 shadow-lg" data-testid="step-1-card">
      <CardHeader>
        <CardTitle className="text-2xl text-[#3D405B] font-['Playfair_Display']">
          {t("booking.step1.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Event Type */}
        <div>
          <Label className="text-[#3D405B] font-medium mb-2 block">
            {t("booking.step1.eventType")}
          </Label>
          <Select
            value={formData.event_type}
            onValueChange={(value) => onUpdateField("event_type", value)}
          >
            <SelectTrigger className="w-full" data-testid="event-type-select">
              <SelectValue placeholder={t("booking.step1.eventTypePlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {eventTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Date Picker */}
        <div>
          <Label className="text-[#3D405B] font-medium mb-2 block">
            {t("booking.step1.eventDate")}
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                data-testid="date-picker-trigger"
                className={`w-full justify-start text-left font-normal ${
                  !date && "text-muted-foreground"
                }`}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(date, "PPP") : t("booking.step1.pickDate")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={onDateSelect}
                disabled={isDateDisabled}
                initialFocus
                data-testid="booking-calendar"
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Guest Count */}
        <div>
          <Label className="text-[#3D405B] font-medium mb-2 block">
            {t("booking.step1.guestCount")}
          </Label>
          <div className="flex items-center gap-4">
            <Input
              type="number"
              value={formData.guest_count}
              onChange={(e) => onGuestCountChange(e.target.value)}
              min={10}
              max={500}
              data-testid="guest-count-input"
              className="w-32"
            />
            <span className="text-[#6B705C] flex items-center gap-2">
              <Users className="w-4 h-4" />
            </span>
          </div>
        </div>

        <Button
          onClick={onNext}
          disabled={!isValid}
          data-testid="next-step-btn"
          className="w-full bg-[#E07A5F] hover:bg-[#D0694E] text-white py-6 rounded-full"
        >
          {t("booking.step1.continueBtn")}
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </CardContent>
    </Card>
  );
};

export default BookingStepEvent;
